// 환생 화면 표시 모델(순수, 레인 R): 모드 카드 3장·보상 줄·배분 줄. DOM·three를 모른다. 규칙은 shared/rebirth.ts.
// 정본 콘셉트: concept/stage-maps-20261023/ui-rebirth.webp(윤회의 문 확인 화면). 보이는 보상은 실제로 적용되는 것만 적는다(날개는 모델 전이라 빼 둔다).
import { REBIRTH } from '../shared/content/items';
import {
  cycleModeName, REBIRTH_POINT_EFFECT, REBIRTH_POINTS_PER_CYCLE, REBIRTH_STATS, REBIRTH_STAT_NAME, REBIRTH_XP_BONUS, REBIRTH_SLOT_BONUS,
  type RebirthAlloc, type RebirthStat,
} from '../shared/rebirth';

export type ModeCardState = 'done' | 'now' | 'next' | 'later' | 'locked';
export interface ModeCard {
  readonly cycle: number;
  /** '1회차' · '1차 환생' · '2차 환생' */
  readonly head: string;
  readonly mode: string;
  readonly state: ModeCardState;
  /** 카드 아래 표지 */
  readonly badge: string;
}
const HEAD = ['1회차', '1차 환생', '2차 환생'];
const BADGE: Readonly<Record<ModeCardState, string>> = { done: '완료', now: '지금', next: '다음 단계', later: '그 다음', locked: '미개방' };

/** 세 모드 카드(지금·다음·그 다음). 지금 회차를 마치고 넘어가는 화면이라 지금 회차는 '지금', 그 앞은 '완료' */
export function modeCards(cycle: number, modes: boolean): ModeCard[] {
  return REBIRTH.map((r) => {
    const state: ModeCardState = r.cycle < cycle ? 'done' : r.cycle === cycle ? 'now' : !(r.released || !!r.fallback?.released || modes) ? 'locked' : r.cycle === cycle + 1 ? 'next' : 'later';
    return { cycle: r.cycle, head: HEAD[r.cycle] ?? r.cycle + '차', mode: cycleModeName(r.cycle, modes), state, badge: BADGE[state] };
  });
}

export interface RewardChip { readonly id: string; readonly icon: string; readonly text: string }
/** 보상 줄(실제로 적용되는 것만) */
export function rewardChips(nextCycle: number): RewardChip[] {
  return [
    { id: 'points', icon: '✦', text: '환생 포인트 +' + REBIRTH_POINTS_PER_CYCLE },
    { id: 'slot', icon: '▣', text: '스킬 슬롯 +' + REBIRTH_SLOT_BONUS + ' (7칸)' },
    { id: 'xp', icon: '▲', text: 'EXP +' + Math.round(REBIRTH_XP_BONUS * 100) + '%' },
    { id: 'look', icon: '★', text: '회차 별 ' + '★'.repeat(Math.max(1, nextCycle)) + ' · 오라' },
  ];
}

export interface AllocRow {
  readonly stat: RebirthStat;
  readonly name: string;
  /** 포인트 1의 효과 문구 */
  readonly per: string;
  readonly value: number;
  /** 지금 배분 효과 문구 */
  readonly effect: string;
  readonly canInc: boolean;
  readonly canDec: boolean;
}
const pctText = (v: number, digits = 0): string => '+' + (v * 100).toFixed(digits) + '%';
export function allocRows(alloc: RebirthAlloc, free: number): AllocRow[] {
  return REBIRTH_STATS.map((stat) => {
    const v = alloc[stat], e = REBIRTH_POINT_EFFECT[stat], crit = stat === 'crit';
    return {
      stat, name: REBIRTH_STAT_NAME[stat], value: v,
      per: '1포인트 ' + (crit ? '+' + (e * 100).toFixed(1) + '%p' : pctText(e)),
      effect: crit ? '+' + (v * e * 100).toFixed(1) + '%p' : pctText(v * e),
      canInc: free > 0, canDec: v > 0,
    };
  });
}
/** 한 칸 올리기·내리기 결과(범위 밖이면 null — 명령을 보내지 않는다) */
export function allocStep(alloc: RebirthAlloc, stat: RebirthStat, delta: number, free: number): RebirthAlloc | null {
  const v = alloc[stat] + delta;
  if (v < 0 || (delta > 0 && free < delta)) return null;
  return { ...alloc, [stat]: v };
}
